import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import {
  useV1ReportingDefinitionsExecuteCreate as useReportDefinitionExecute,
  useV1ReportingDefinitionsRetrieve as useReportDefinitionRetrieve,
} from "@/api/generated/v1/v1";
import { PageBanner } from "@/shared/components/PageBanner";

export function buildReportExportUrl(id: string): string {
  const baseURL = import.meta.env.VITE_API_BASE_URL || "";
  return `${baseURL}/api/v1/reporting/definitions/${id}/export/`;
}

import { customAxiosInstance } from "@/api/client";

const formatCellValue = (val: unknown) => {
  if (val === null || val === undefined) return "";
  const strVal = String(val);

  if (/^\d{4}-\d{2}-\d{2}$/.test(strVal)) {
    const d = new Date(strVal + "T12:00:00");
    return d.toLocaleDateString("fa-IR");
  }

  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(strVal)) {
    const d = new Date(strVal);
    if (!isNaN(d.getTime())) {
      return d.toLocaleString("fa-IR");
    }
  }

  return strVal;
};

export function ReportResultPage() {
  const { id } = useParams<{ id: string }>();
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [isExporting, setIsExporting] = useState(false);

  const { data: definition } = useReportDefinitionRetrieve(id ?? "", { query: { enabled: !!id } });
  const executeMutation = useReportDefinitionExecute();

  useEffect(() => {
    if (id) {
      executeMutation.mutate({ id, params: { page, page_size: pageSize } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, page]);

  const handleExport = async () => {
    if (!id) return;
    setIsExporting(true);
    try {
      const response = await customAxiosInstance({
        url: `/api/v1/reporting/definitions/${id}/export/`,
        method: "GET",
        responseType: "blob", // To handle the CSV file
      });
      // Create a blob URL and trigger download
      const blob = new Blob([response as unknown as BlobPart], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(definition as any)?.name ?? "report"}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      console.error("Export failed", err);
      alert("خطا در دریافت فایل خروجی.");
    } finally {
      setIsExporting(false);
    }
  };

  const result: any = executeMutation.data;
  const rows: Record<string, unknown>[] = result?.rows ?? result?.data?.rows ?? [];
  const columns: string[] = result?.columns ?? result?.data?.columns ?? [];
  const totalCount: number = result?.total_count ?? result?.data?.total_count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const def: any = definition;

  return (
    <div style={{ padding: "0.5rem 0", maxWidth: "100%", margin: "0", display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <PageBanner
        title={def?.name ?? "در حال بارگذاری..."}
        subtitle={`${totalCount} ردیف — مشاهده نتایج گزارش`}
        icon="📋"
        helpLink="/help/reporting/results"
      >
        <div style={{ display: "flex", gap: "0.75rem", alignItems: "center" }}>
          {id && (
            <>
              <button
                onClick={handleExport}
                disabled={isExporting}
                style={{
                  padding: "0.6rem 1.25rem",
                  borderRadius: "12px",
                  background: "#ffffff",
                  color: "#007270",
                  fontWeight: 700,
                  fontSize: "0.9rem",
                  border: "none",
                  cursor: isExporting ? "not-allowed" : "pointer",
                  opacity: isExporting ? 0.7 : 1,
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  transition: "all 0.2s ease",
                }}
              >
                <span>📥</span>
                <span>{isExporting ? "در حال دریافت..." : "خروجی CSV"}</span>
              </button>
              <Link
                to={`/reporting/${id}/edit`}
                style={{
                  padding: "0.6rem 1rem",
                  borderRadius: "12px",
                  background: "rgba(255, 255, 255, 0.15)",
                  color: "#ffffff",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                  fontWeight: 700,
                  fontSize: "0.85rem",
                  textDecoration: "none",
                }}
              >
                ✏️ ویرایش گزارش
              </Link>
            </>
          )}
        </div>
      </PageBanner>

      <div className="brand-card-extand" style={{ padding: "1.5rem" }}>

      {executeMutation.isPending && <p className="text-sm text-gray-500">در حال اجرا...</p>}
      {executeMutation.isError && (
        <p className="text-sm text-red-600">
          {(executeMutation.error as any)?.response?.data?.detail ?? "خطا در اجرای گزارش."}
        </p>
      )}

      {!executeMutation.isPending && rows.length > 0 && (
        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                {columns.map((c) => (
                  <th key={c} className="p-2 text-right font-medium">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              {rows.map((row, i) => (
                <tr key={i}>
                  {columns.map((c) => (
                    <td key={c} className="p-2">
                      {formatCellValue(row[c])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!executeMutation.isPending && rows.length === 0 && !executeMutation.isError && (
        <p className="text-sm text-gray-500">نتیجه‌ای یافت نشد.</p>
      )}

      {totalPages > 1 && (
        <div className="flex items-center gap-2 mt-4">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="rounded border px-3 py-1 text-sm disabled:opacity-50"
          >
            قبلی
          </button>
          <span className="text-sm">
            صفحهٔ {page} از {totalPages}
          </span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
            className="rounded border px-3 py-1 text-sm disabled:opacity-50"
          >
            بعدی
          </button>
        </div>
      )}
      </div>
    </div>
  );
}
